import React, { useMemo, useRef, useState } from 'react';
import InfoTooltip from '../../common/InfoTooltip';
import PatientLimitContactModal from './PatientLimitContactModal';
import PatientLimitDropJustificationModal from './PatientLimitDropJustificationModal';
import PatientLimitIncreaseBlockedModal from './PatientLimitIncreaseBlockedModal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { usePsychologistContext } from '../../../contexts/PsychologistContext';
import { PENDING_DISCHARGED_STATUS_ID } from '../../../utils/psychologistStatus';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import { getPsychologists } from '@/hooks/api/psychologist/list';
import useToast from '@/hooks/useToast';

import { activityTracker } from '../../../utils/activityTracker';
interface PatientLimitFieldProps {
  /** Valor draft actual (lo que la psicóloga ve y puede modificar) */
  value: number;
  /** Límite interno (process_limit). null mientras carga / si no está disponible. */
  processLimit: number | null;
  /** Cantidad de procesos vigentes (active + retention). null mientras carga / si falla. */
  currentAssignedProcesses: number | null;
  /** Llamado cuando el draft válido cambia (todavía pendiente de "Guardar Cambios") */
  onChange: (next: number) => void;
  /**
   * Llamado cuando una BAJA ya quedó aplicada de verdad (ver handleConfirmDropJustification):
   * a diferencia de onChange, esto no es un draft — el backend ya confirmó el cambio real,
   * así que el padre debe sincronizar tanto el draft como el valor "guardado" para que no
   * quede una diferencia pendiente esperando un "Guardar Cambios" que ya no hace falta.
   */
  onApplied: (next: number) => void;
  /** True cuando el draft difiere del valor guardado en backend (cambio pendiente) */
  isModified: boolean;
  /** Necesario para persistir la justificación de baja de preferencia. null si aún no se conoce. */
  psychologistId: string | null;
  /** Límite interno configurado por Psi Mammoliti. Si value > internalLimit se muestra advertencia. */
  internalLimit: number | null;
}

const SELECT_MIN = 5;
const SELECT_MAX = 40;
/** A partir de esta baja (30% o más) respecto del límite interno, no se puede autogestionar. */
const DROP_FRICTION_THRESHOLD = 0.30;

type DropDecision = 'none' | 'selfService' | 'contactTeam';

/** Fracción (0-1) de baja respecto del límite interno. Único criterio de fricción/bloqueo —
 *  ya no importa cuántos procesos activos tenga la psicóloga hoy. */
function computeDropRatio(processLimit: number | null, newValue: number): number {
  if (processLimit == null || processLimit <= 0 || processLimit <= newValue) return 0;
  return (processLimit - newValue) / processLimit;
}

export const PatientLimitField: React.FC<PatientLimitFieldProps> = ({
  value,
  processLimit,
  currentAssignedProcesses,
  onChange,
  onApplied,
  isModified,
  psychologistId,
  internalLimit,
}) => {
  const { psychologist } = usePsychologistContext();
  const { showError } = useToast();
  const lastValidRef = useRef<number>(value);

  const [modalState, setModalState] = useState<{ open: boolean; attempted: number; dropPercentage: number }>(
    { open: false, attempted: 0, dropPercentage: 0 },
  );
  const [dropModalState, setDropModalState] = useState<{ open: boolean; newValue: number | null }>(
    { open: false, newValue: null },
  );
  const [submittingJustification, setSubmittingJustification] = useState(false);
  const [increaseBlockedOpen, setIncreaseBlockedOpen] = useState(false);

  const options = useMemo(() => {
    const arr: number[] = [];
    for (let i = SELECT_MIN; i <= SELECT_MAX; i += 1) arr.push(i);
    return arr;
  }, []);

  const isPendingDischarged = psychologist?.statusId === PENDING_DISCHARGED_STATUS_ID;
  /**
   * Regla de fricción al subir la preferencia para psicólogas en pending_discharged: no aplica a
   * N/NZ porque para ellas toda la sección ya está oculta más abajo, pero se deja explícito acá
   * para no depender implícitamente de ese early return.
   */
  const isPendingDischargeIncreaseBlocked = isPendingDischarged
    && psychologist?.rankingCategory !== 'N'
    && psychologist?.rankingCategory !== 'NZ';

  /**
   * Único criterio de fricción/bloqueo al bajar la preferencia, siempre calculado contra el
   * límite interno (`processLimit`), nunca contra la preferencia anterior ni contra la cantidad
   * de procesos activos actuales: dropRatio = (processLimit - newValue) / processLimit. Por
   * debajo del 30% de baja es autogestionable (con motivo); 30% o más requiere coordinación con
   * el equipo. Excepción total: si el estado es "pending_discharged" nunca se evalúa esta regla
   * — esto es una regla de backend y no debe insinuarse en el frontend.
   */
  const evaluateDrop = (newValue: number): DropDecision => {
    const dropRatio = computeDropRatio(processLimit, newValue);
    if (dropRatio <= 0) return 'none';
    return dropRatio >= DROP_FRICTION_THRESHOLD ? 'contactTeam' : 'selfService';
  };

  /**
   * Registro de auditoría best-effort para los dos casos que hasta ahora no dejaban ningún
   * rastro consultable: el bloqueo duro (≥30%, el valor nunca se aplica) y la fricción
   * autogestionable cancelada sin confirmar. Fire-and-forget a propósito — es un log en
   * paralelo, nunca debe interrumpir ni demorar el flujo real de la psicóloga (por eso no
   * usa showError ni bloquea ningún botón, a diferencia de handleConfirmDropJustification).
   */
  const logPatientLimitAttempt = (outcome: 'blocked' | 'cancelled', newValue: number) => {
    if (psychologistId == null) return;
    apiCoreAdapter
      .psychologistPatientLimitJustification({
        psychologist_id: psychologistId,
        previous_value: lastValidRef.current,
        new_value: newValue,
        drop_percentage: computeDropRatio(processLimit, newValue),
        outcome,
      })
      .catch(() => { /* auditoría best-effort — no debe interrumpir el flujo real */ });
  };

  const handleSelect = (raw: string) => {
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clampedTop = Math.min(parsed, SELECT_MAX);

    if (isPendingDischargeIncreaseBlocked && clampedTop > value) {
      setIncreaseBlockedOpen(true);
      return;
    }

    if (clampedTop === lastValidRef.current) return;

    if (!isPendingDischarged) {
      const decision = evaluateDrop(clampedTop);
      if (decision === 'contactTeam') {
        const dropPercentage = Math.round(computeDropRatio(processLimit, clampedTop) * 100);
        setModalState({ open: true, attempted: clampedTop, dropPercentage });
        logPatientLimitAttempt('blocked', clampedTop);
        return;
      }
      if (decision === 'selfService') {
        setDropModalState({ open: true, newValue: clampedTop });
        return;
      }
    }

    lastValidRef.current = clampedTop;
    onChange(clampedTop);
  };

  const dropRatio = dropModalState.newValue != null ? computeDropRatio(processLimit, dropModalState.newValue) : 0;
  const dropPercentage = Math.round(dropRatio * 100);

  const closeDropModal = () => {
    if (submittingJustification) return;
    if (dropModalState.newValue != null) {
      logPatientLimitAttempt('cancelled', dropModalState.newValue);
    }
    setDropModalState({ open: false, newValue: null });
  };

  const handleConfirmDropJustification = async (reason: string) => {
    const newValue = dropModalState.newValue;
    if (newValue == null || psychologistId == null) return;

    setSubmittingJustification(true);
    try {
      const justification = await apiCoreAdapter.psychologistPatientLimitJustification({
        psychologist_id: psychologistId,
        previous_value: lastValidRef.current,
        new_value: newValue,
        drop_percentage: dropRatio,
        outcome: 'confirmed',
        reason,
      });

      // La baja se aplica YA en este mismo paso — no queda pendiente del botón
      // "Guardar Cambios" de más abajo en la página. Antes este modal solo dejaba el
      // motivo guardado y actualizaba un draft local; si la psicóloga no llegaba a
      // guardar el perfil completo, el cambio real nunca se aplicaba, aunque el equipo
      // ya hubiera sido notificado (le pasó dos veces a una psicóloga real). Ahora el
      // mail a administracion@ sale recién desde el backend cuando este PATCH se confirma, así
      // que aplicarlo acá garantiza que nunca puede haber mail sin cambio real.
      let limitForPatch = processLimit;
      if (typeof limitForPatch !== 'number') {
        const resp = await getPsychologists({ id: psychologistId, take: 1 });
        limitForPatch = resp.data.find(p => String(p.id) === String(psychologistId))?.process_limit ?? null;
      }
      if (typeof limitForPatch !== 'number') {
        throw new Error('missing internal limit');
      }
      await apiCoreAdapter.updatePsychologistPatientLimit(psychologistId, {
        process_limit: limitForPatch,
        process_limit_preferred: newValue,
        // Liga el aviso a administracion@ (backend) con el motivo exacto de este cambio, en vez
        // de que el backend tenga que adivinarlo por valor — ver el comentario en
        // profile-extras.service.ts sobre por qué eso podía mandar un motivo viejo.
        justification_id: justification.id,
      });
      activityTracker.trackAction('patient_limit_changed'); // Métricas de Actividad: sin el valor ni la justificación

      lastValidRef.current = newValue;
      onApplied(newValue);
      setDropModalState({ open: false, newValue: null });
    } catch {
      showError('No pudimos aplicar el cambio. Probá de nuevo.');
    } finally {
      setSubmittingJustification(false);
    }
  };

  // Categorías N/NZ no autogestionan cupo — se oculta toda la sección (incluida, por lo tanto,
  // la fricción/retention por límite interno de arriba, que nunca llega a evaluarse para ellas).
  if (psychologist?.rankingCategory === 'N' || psychologist?.rankingCategory === 'NZ') return null;

  return (
    <div className="tags-container">
      <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
        <p className="text-sm sm:text-base font-medium text-gray-700 m-0">Cantidad de pacientes</p>
        <InfoTooltip
          ariaLabel="Información sobre la cantidad de pacientes"
          text={
            <div className="flex flex-col gap-3">
              <div>
                <p className="font-bold text-gray-400 uppercase tracking-wider m-0 mb-1">Tu preferencia</p>
                <p className="m-0">Indicar tu tope preferido nos ayuda a respetar tu carga ideal. No es una garantía de que recibirás esa cantidad exacta, pero queda registrado y se considera al asignarte nuevos pacientes.</p>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <p className="font-bold text-gray-400 uppercase tracking-wider m-0 mb-1">¿Qué es un proceso activo?</p>
                <p className="m-0">Es un proceso terapéutico que tuvo al menos una sesión en los últimos 30 días — ese es el criterio que usamos para contar cuántos pacientes tenés en curso ahora mismo. No incluye a ex pacientes: si alguien que dejó de atenderse con vos vuelve a buscarte, siempre lo vas a poder recibir, aunque eso implique superar tu preferencia.</p>
              </div>
            </div>
          }
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Tu preferencia */}
        <div className="rounded-xl bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider m-0">
              Tu preferencia
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={String(value)} onValueChange={handleSelect}>
              <SelectTrigger
                aria-label="Cantidad preferida de pacientes"
                className={`h-10 w-24 rounded-lg border bg-white px-3 text-2xl font-semibold text-gray-700 focus:ring-2 focus:ring-gray-200 ${
                  isModified ? 'border-amber-400 ring-1 ring-amber-200' : 'border-gray-200'
                }`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[220px]" scrollButtonBehavior="slow">
                {options.map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-sm py-2">
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isModified && psychologist?.rankingCategory === 'N' && internalLimit != null && value > internalLimit && (
              <InfoTooltip
                iconColor="#d97706"
                ariaLabel="Advertencia sobre el límite de pacientes"
                text={`Vas a recibir hasta ${internalLimit} pacientes, ya que es el límite configurado por Psi Mammoliti.`}
                placement="top"
              />
            )}
          </div>
        </div>

        {/* Procesos activos */}
        <div className="rounded-xl bg-gray-50 px-4 py-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 m-0">
            Procesos activos
          </p>
          <div className="h-10 flex items-center">
            <p className="text-2xl font-semibold text-gray-700 m-0 leading-none">
              {currentAssignedProcesses ?? '—'}
            </p>
          </div>
        </div>
      </div>

      <PatientLimitContactModal
        open={modalState.open}
        onClose={() => setModalState({ open: false, attempted: 0, dropPercentage: 0 })}
        attemptedValue={modalState.attempted}
        processLimit={processLimit}
        dropPercentage={modalState.dropPercentage}
      />

      <PatientLimitDropJustificationModal
        open={dropModalState.open}
        dropPercentage={dropPercentage}
        submitting={submittingJustification}
        onCancel={closeDropModal}
        onConfirm={handleConfirmDropJustification}
      />

      <PatientLimitIncreaseBlockedModal
        open={increaseBlockedOpen}
        onClose={() => setIncreaseBlockedOpen(false)}
      />
    </div>
  );
};

export default PatientLimitField;
